import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ClipboardCheck, ShieldAlert, FileStack, ListChecks, Settings2, Users, ArrowLeft,
  Plus, Trash2, CheckCircle2, XCircle, ChevronRight, AlertTriangle, Layers, FileWarning, Wrench, ClipboardList, Download,
} from 'lucide-react';
import EvidenceButton from '../sitewalk/EvidenceButton';
import { InspectionRequestPanel, NcrPanel, CorrectiveActionPanel, SafetyObservationDrawer, AiReviewQueue } from './QSWorkflows';
import CameraEvidence from './CameraEvidence';
import QSManagement from './QSManagement';
import { useOffline } from '../../contexts/OfflineContext';
import { queueQsOperation, newClientKey } from '../../lib/qsOffline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function downloadPdf(url, filename) {
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u);
  } catch (e) { toast.error('Report not permitted'); }
}

const RESPONSE_TYPES = ['PASS_FAIL_NA', 'YESNO_NA', 'COMPLIANT_NA', 'NUMERIC', 'TEXT', 'DATE', 'SINGLE_SELECT', 'MULTI_SELECT'];
const EVIDENCE_RULES = ['OPTIONAL', 'REQUIRED', 'REQUIRED_ON_FAILURE'];
const FUNCTIONAL_ROLES = ['QAQC_ENGINEER', 'QAQC_MANAGER', 'SITE_ENGINEER', 'SAFETY_OFFICER', 'HSE_MANAGER', 'PROJECT_MANAGER', 'CONTRACTOR', 'AUDITOR'];
const Q_OBS_TYPES = ['SNAG', 'PUNCH', 'DEFECT'];
const S_OBS_CATS = ['UNSAFE_ACT', 'UNSAFE_CONDITION', 'NEAR_MISS', 'HAZARD', 'POSITIVE'];

const badge = (s) => ({
  ACTIVE: 'bg-green-100 text-green-700', SUPERSEDED: 'bg-slate-100 text-slate-500', ARCHIVED: 'bg-slate-100 text-slate-400',
  DRAFT: 'bg-amber-100 text-amber-700', IN_PROGRESS: 'bg-blue-100 text-blue-700', SUBMITTED: 'bg-purple-100 text-purple-700',
  REVIEWED: 'bg-green-100 text-green-700', PASS: 'bg-green-100 text-green-700', FAIL: 'bg-red-100 text-red-700',
  CONDITIONAL: 'bg-amber-100 text-amber-700', OPEN: 'bg-red-100 text-red-700', PENDING: 'bg-slate-100 text-slate-600',
}[s] || 'bg-slate-100 text-slate-600');

export default function QSHub({ domain }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isQuality = domain === 'QUALITY';
  const accent = isQuality ? 'blue' : 'rose';
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [observations, setObservations] = useState([]);
  const [config, setConfig] = useState(null);
  const [roles, setRoles] = useState([]);
  const [team, setTeam] = useState([]);
  const [parties, setParties] = useState([]);
  const [activeInsp, setActiveInsp] = useState(null);
  const [canManageRoles, setCanManageRoles] = useState(false);

  const dashUrl = isQuality ? 'quality/dashboard' : 'safety/dashboard';
  const obsUrl = isQuality ? 'quality/observations' : 'safety/observations';

  const load = useCallback(async () => {
    try { setDash((await axios.get(`${API}/projects/${projectId}/${dashUrl}`)).data); } catch (e) {}
    try { setTemplates((await axios.get(`${API}/projects/${projectId}/qs/templates`, { params: { domain } })).data); } catch (e) {}
    try { setInspections((await axios.get(`${API}/projects/${projectId}/qs/inspections`, { params: { domain } })).data.items); } catch (e) {}
    try { setObservations((await axios.get(`${API}/projects/${projectId}/${obsUrl}`)).data.items); } catch (e) {}
    try { setParties((await axios.get(`${API}/projects/${projectId}/qs/parties`)).data); } catch (e) {}
  }, [projectId, domain, dashUrl, obsUrl]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/qs/roles`).then((r) => { setRoles(r.data); setCanManageRoles(true); }).catch(() => setCanManageRoles(false));
    axios.get(`${API}/projects/${projectId}/qs/config`).then((r) => setConfig(r.data)).catch(() => {});
    axios.get(`${API}/team`).then((r) => setTeam(r.data)).catch(() => {});
  }, [projectId]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: isQuality ? ClipboardCheck : ShieldAlert },
    ...(isQuality ? [{ id: 'requests', label: 'Inspection Requests', icon: ClipboardList }] : []),
    { id: 'inspections', label: 'Inspections', icon: ListChecks },
    { id: 'observations', label: 'Observations', icon: AlertTriangle },
    ...(isQuality ? [{ id: 'ncrs', label: 'NCRs', icon: FileWarning }] : []),
    { id: 'actions', label: 'Corrective Actions', icon: Wrench },
    { id: 'ai', label: 'AI Review Queue', icon: ShieldAlert },
    { id: 'management', label: 'Management', icon: ClipboardCheck },
    { id: 'templates', label: 'Templates', icon: FileStack },
    { id: 'config', label: 'Configuration', icon: Settings2 },
    ...(canManageRoles ? [{ id: 'roles', label: 'Team & Roles', icon: Users }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`qs-hub-${domain.toLowerCase()}`}>
      <div className={`bg-${accent}-700 text-white px-4 sm:px-8 py-5`}>
        <button onClick={() => navigate('/app/projects')} className="flex items-center gap-1 text-white/80 text-sm mb-2 hover:text-white" data-testid="qs-back-btn">
          <ArrowLeft className="w-4 h-4" /> Projects
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          {isQuality ? <ClipboardCheck className="w-6 h-6" /> : <ShieldAlert className="w-6 h-6" />}
          {isQuality ? 'Quality Intelligence' : 'Safety Intelligence'}
        </h1>
        <p className="text-white/70 text-sm mt-1">Governed inspections, checklists & observations — human-authoritative records.</p>
      </div>

      <div className="border-b bg-white overflow-x-auto">
        <div className="flex px-2 sm:px-6">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`qs-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? `border-${accent}-600 text-${accent}-700 font-semibold` : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {tab === 'dashboard' && <Dashboard dash={dash} isQuality={isQuality} />}
        {tab === 'requests' && <InspectionRequestPanel projectId={projectId} templates={templates} accent={accent} />}
        {tab === 'templates' && <Templates domain={domain} projectId={projectId} templates={templates} reload={load} accent={accent} />}
        {tab === 'inspections' && <Inspections domain={domain} projectId={projectId} inspections={inspections} templates={templates} parties={parties} team={team} reload={load} onOpen={setActiveInsp} accent={accent} />}
        {tab === 'observations' && <Observations domain={domain} projectId={projectId} obsUrl={obsUrl} observations={observations} team={team} parties={parties} inspections={inspections} reload={load} accent={accent} />}
        {tab === 'ncrs' && <NcrPanel projectId={projectId} parties={parties} templates={templates} />}
        {tab === 'actions' && <CorrectiveActionPanel projectId={projectId} domain={isQuality ? 'quality' : 'safety'} parties={parties} accent={accent} />}
        {tab === 'ai' && <AiReviewQueue projectId={projectId} domain={domain} />}
        {tab === 'management' && <QSManagement projectId={projectId} domain={domain} accent={accent} />}
        {tab === 'config' && <Configuration projectId={projectId} config={config} setConfig={setConfig} accent={accent} />}
        {tab === 'roles' && <RolesPanel projectId={projectId} roles={roles} team={team} parties={parties} reload={() => axios.get(`${API}/projects/${projectId}/qs/roles`).then((r) => setRoles(r.data))} accent={accent} />}
      </div>

      {activeInsp && (
        <InspectionDrawer projectId={projectId} inspectionId={activeInsp} onClose={() => { setActiveInsp(null); load(); }} accent={accent} />
      )}
    </div>
  );
}

function Kpi({ label, value, tone = 'slate' }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid={`qs-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-2xl font-bold text-${tone}-700 mt-1`}>{value ?? '—'}</p>
    </div>
  );
}

function Dashboard({ dash, isQuality }) {
  if (!dash) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="qs-dashboard">
      {isQuality ? (
        <>
          <Kpi label="Open Observations" value={dash.openObservations} tone="red" />
          <Kpi label="Inspections" value={dash.inspectionsTotal} tone="blue" />
          <Kpi label="Pending Review" value={dash.inspectionsPendingReview} tone="purple" />
          <Kpi label="Failed Inspections" value={dash.inspectionsFailed} tone="red" />
          <Kpi label="Active Templates" value={dash.activeTemplates} />
          <Kpi label="Total Observations" value={dash.totalObservations} />
        </>
      ) : (
        <>
          <Kpi label="Open Observations" value={dash.openObservations} tone="red" />
          <Kpi label="Near Misses" value={dash.nearMisses} tone="amber" />
          <Kpi label="Unsafe Conditions" value={dash.unsafeConditions} tone="red" />
          <Kpi label="Inspections" value={dash.inspectionsTotal} tone="rose" />
          <Kpi label="Pending Review" value={dash.inspectionsPendingReview} tone="purple" />
          <Kpi label="Active Templates" value={dash.activeTemplates} />
        </>
      )}
    </div>
  );
}

/* ------------------------------- Templates ------------------------------- */
function Templates({ domain, projectId, templates, reload, accent }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [items, setItems] = useState([{ text: '', responseType: 'PASS_FAIL_NA', evidenceRule: 'OPTIONAL', acceptanceCriteria: '' }]);

  const addItem = () => setItems([...items, { text: '', responseType: 'PASS_FAIL_NA', evidenceRule: 'OPTIONAL', acceptanceCriteria: '' }]);
  const setItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim() || items.some((it) => !it.text.trim())) return toast.error('Name and all item texts are required');
    try {
      await axios.post(`${API}/projects/${projectId}/qs/templates`, { domain, name, code, items });
      toast.success('Template created'); setCreating(false); setName(''); setCode('');
      setItems([{ text: '', responseType: 'PASS_FAIL_NA', evidenceRule: 'OPTIONAL', acceptanceCriteria: '' }]); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };
  const newVersion = async (t) => {
    try { await axios.post(`${API}/projects/${projectId}/qs/templates/${t.id}/version`, { items: t.items }); toast.success(`New version created (v${t.version + 1})`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="qs-templates">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Checklist Templates</h2>
        <button onClick={() => setCreating(!creating)} data-testid="qs-new-template-btn" className={`flex items-center gap-1 px-3 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}><Plus className="w-4 h-4" /> New Template</button>
      </div>

      {creating && (
        <div className="bg-white rounded-xl border p-4 mb-4 space-y-3" data-testid="qs-template-form">
          <div className="grid grid-cols-2 gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name (e.g. Masonry Inspection)" data-testid="qs-template-name" className="border rounded-lg px-3 py-2 text-sm" />
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code (optional, e.g. MAS-INS)" data-testid="qs-template-code" className="border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="border rounded-lg p-2 space-y-2" data-testid={`qs-template-item-${i}`}>
                <div className="flex gap-2">
                  <input value={it.text} onChange={(e) => setItem(i, 'text', e.target.value)} placeholder={`Item ${i + 1} text`} className="flex-1 border rounded px-2 py-1.5 text-sm" data-testid={`qs-item-text-${i}`} />
                  <button onClick={() => removeItem(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select value={it.responseType} onChange={(e) => setItem(i, 'responseType', e.target.value)} className="border rounded px-2 py-1.5 text-xs" data-testid={`qs-item-rt-${i}`}>
                    {RESPONSE_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select value={it.evidenceRule} onChange={(e) => setItem(i, 'evidenceRule', e.target.value)} className="border rounded px-2 py-1.5 text-xs" data-testid={`qs-item-ev-${i}`}>
                    {EVIDENCE_RULES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <input value={it.acceptanceCriteria} onChange={(e) => setItem(i, 'acceptanceCriteria', e.target.value)} placeholder="Acceptance criteria (optional)" className="w-full border rounded px-2 py-1.5 text-xs" />
              </div>
            ))}
            <button onClick={addItem} className="text-sm text-slate-600 flex items-center gap-1" data-testid="qs-add-item-btn"><Plus className="w-4 h-4" /> Add item</button>
          </div>
          <button onClick={save} data-testid="qs-save-template-btn" className={`px-4 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}>Save Template</button>
        </div>
      )}

      <div className="space-y-2">
        {templates.length === 0 && <p className="text-slate-400 text-sm">No templates yet.</p>}
        {templates.map((t) => (
          <div key={t.id} className="bg-white rounded-lg border p-3 flex items-center justify-between" data-testid={`qs-template-row-${t.id}`}>
            <div>
              <p className="font-medium text-slate-800 text-sm">{t.name} <span className="text-slate-400">· {t.code} · v{t.version}</span></p>
              <p className="text-xs text-slate-500">{t.items?.length || 0} checklist items</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded ${badge(t.status)}`}>{t.status}</span>
              {t.status === 'ACTIVE' && <button onClick={() => newVersion(t)} data-testid={`qs-new-version-${t.id}`} className="text-xs flex items-center gap-1 text-slate-600 border rounded px-2 py-1"><Layers className="w-3 h-3" /> New Version</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- Inspections ------------------------------- */
function Inspections({ domain, projectId, inspections, templates, parties, team, reload, onOpen, accent }) {
  const [creating, setCreating] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [orgId, setOrgId] = useState('');
  const active = templates.filter((t) => t.status === 'ACTIVE');

  const create = async () => {
    if (!templateId) return toast.error('Select a template');
    try {
      const r = await axios.post(`${API}/projects/${projectId}/qs/inspections`, { domain, templateId, title, responsibleOrgId: orgId || null });
      toast.success(`Created ${r.data.inspectionNumber}`); setCreating(false); setTitle(''); setTemplateId(''); reload(); onOpen(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="qs-inspections">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Inspections</h2>
        <button onClick={() => setCreating(!creating)} data-testid="qs-new-inspection-btn" className={`flex items-center gap-1 px-3 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}><Plus className="w-4 h-4" /> New Inspection</button>
      </div>
      {creating && (
        <div className="bg-white rounded-xl border p-4 mb-4 space-y-3" data-testid="qs-inspection-form">
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qs-inspection-template">
            <option value="">Select checklist template…</option>
            {active.map((t) => <option key={t.id} value={t.id}>{t.name} (v{t.version})</option>)}
          </select>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title / location (e.g. Tower B · Floor 5 · Masonry)" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qs-inspection-title" />
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qs-inspection-org">
            <option value="">Responsible organization (optional)</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.type})</option>)}
          </select>
          <button onClick={create} data-testid="qs-create-inspection-btn" className={`px-4 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}>Create & Open</button>
        </div>
      )}
      <div className="space-y-2">
        {inspections.length === 0 && <p className="text-slate-400 text-sm">No inspections yet.</p>}
        {inspections.map((it) => (
          <button key={it.id} onClick={() => onOpen(it.id)} data-testid={`qs-inspection-row-${it.id}`} className="w-full bg-white rounded-lg border p-3 flex items-center justify-between text-left hover:shadow-sm">
            <div>
              <p className="font-medium text-slate-800 text-sm">{it.inspectionNumber} · {it.title}</p>
              <p className="text-xs text-slate-500">Template {it.templateCode} v{it.templateVersion}{it.followUp?.required ? ' · has failed items' : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded ${badge(it.status)}`}>{it.status}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${badge(it.overallResult)}`}>{it.overallResult}</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function InspectionDrawer({ projectId, inspectionId, onClose, accent }) {
  const [insp, setInsp] = useState(null);
  const [resp, setResp] = useState({});
  const load = useCallback(() => {
    axios.get(`${API}/projects/${projectId}/qs/inspections/${inspectionId}`).then((r) => {
      setInsp(r.data);
      const map = {}; (r.data.responses || []).forEach((x) => { map[x.itemId] = x; });
      setResp(map);
    }).catch(() => {});
  }, [projectId, inspectionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!insp) return null;
  const items = insp.templateSnapshot.items;
  const locked = insp.status === 'REVIEWED';
  const setVal = (id, k, v) => setResp({ ...resp, [id]: { ...(resp[id] || { itemId: id }), itemId: id, [k]: v } });

  const saveResponses = async () => {
    try { await axios.post(`${API}/projects/${projectId}/qs/inspections/${inspectionId}/responses`, { responses: Object.values(resp) }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const submit = async (result) => {
    try { await axios.post(`${API}/projects/${projectId}/qs/inspections/${inspectionId}/responses`, { responses: Object.values(resp) });
      await axios.post(`${API}/projects/${projectId}/qs/inspections/${inspectionId}/submit`, { overallResult: result }); toast.success('Submitted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Cannot submit'); }
  };
  const review = async (decision) => {
    try { await axios.post(`${API}/projects/${projectId}/qs/inspections/${inspectionId}/review`, { decision }); toast.success(`Inspection ${decision.toLowerCase()}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Cannot review'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose} data-testid="qs-inspection-drawer">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-bold text-slate-800">{insp.inspectionNumber}</h3>
            <p className="text-sm text-slate-500">{insp.title} · {insp.templateCode} v{insp.templateVersion}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => downloadPdf(`${API}/projects/${projectId}/qs/inspections/${inspectionId}/report.pdf`, `${insp.inspectionNumber}.pdf`)} data-testid="qs-inspection-pdf-btn" className="text-xs flex items-center gap-1 border rounded px-2 py-1 text-slate-600"><Download className="w-3.5 h-3.5" /> PDF</button>
            <button onClick={onClose} className="text-slate-400"><XCircle className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <span className={`text-xs px-2 py-0.5 rounded ${badge(insp.status)}`}>{insp.status}</span>
          <span className={`text-xs px-2 py-0.5 rounded ${badge(insp.overallResult)}`}>Result: {insp.overallResult}</span>
        </div>

        <div className="space-y-3">
          {items.map((it) => {
            const r = resp[it.itemId] || {};
            return (
              <div key={it.itemId} className="border rounded-lg p-3" data-testid={`qs-resp-item-${it.itemId}`}>
                <p className="text-sm font-medium text-slate-800">{it.text}</p>
                {it.acceptanceCriteria && <p className="text-xs text-slate-400 mb-1">Criteria: {it.acceptanceCriteria}</p>}
                <p className="text-[10px] text-slate-400 mb-2">{it.responseType} · evidence {it.evidenceRule}{it.measurementRequired ? ' · measurement req' : ''}</p>
                <ResponseInput item={it} value={r.value} disabled={locked} onChange={(v) => setVal(it.itemId, 'value', v)} accent={accent} />
                {it.measurementRequired && <input disabled={locked} value={r.measurement || ''} onChange={(e) => setVal(it.itemId, 'measurement', e.target.value)} placeholder="Measurement" className="mt-2 w-full border rounded px-2 py-1 text-xs" data-testid={`qs-measure-${it.itemId}`} />}
                {!locked && <div className="mt-2"><CameraEvidence projectId={projectId} evidenceIds={r.evidenceIds || []} onChange={(v) => setVal(it.itemId, 'evidenceIds', v)} testId={`qs-item-evi-${it.itemId}`} /></div>}
                <input disabled={locked} value={r.comment || ''} onChange={(e) => setVal(it.itemId, 'comment', e.target.value)} placeholder="Comment" className="mt-2 w-full border rounded px-2 py-1 text-xs" data-testid={`qs-comment-${it.itemId}`} />
              </div>
            );
          })}
        </div>

        {!locked && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={saveResponses} data-testid="qs-save-responses-btn" className="px-3 py-2 border rounded-lg text-sm">Save Progress</button>
            <button onClick={() => submit('PASS')} data-testid="qs-submit-pass-btn" className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm">Submit as PASS</button>
            <button onClick={() => submit('FAIL')} data-testid="qs-submit-fail-btn" className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm">Submit as FAIL</button>
            <button onClick={() => submit('CONDITIONAL')} data-testid="qs-submit-cond-btn" className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm">Conditional</button>
          </div>
        )}
        {insp.status === 'SUBMITTED' && (
          <div className="mt-4 border-t pt-3">
            <p className="text-xs text-slate-500 mb-2">Review (requires review permission; separation of duties applies if enabled)</p>
            <div className="flex gap-2">
              <button onClick={() => review('APPROVED')} data-testid="qs-review-approve-btn" className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Approve</button>
              <button onClick={() => review('REJECTED')} data-testid="qs-review-reject-btn" className="px-3 py-2 border rounded-lg text-sm">Reject</button>
            </div>
          </div>
        )}
        {insp.followUp?.required && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="qs-followup">
            <p className="text-xs font-semibold text-amber-700">Failed items flagged for downstream Quality action:</p>
            <ul className="text-xs text-amber-700 list-disc ml-4 mt-1">{insp.followUp.failedItems.map((f) => <li key={f.itemId}>{f.text}</li>)}</ul>
          </div>
        )}
      </div>
    </div>
  );
}

function ResponseInput({ item, value, disabled, onChange, accent }) {
  const rt = item.responseType;
  const opts = { PASS_FAIL_NA: ['PASS', 'FAIL', 'NA'], YESNO_NA: ['YES', 'NO', 'NA'], COMPLIANT_NA: ['COMPLIANT', 'NON_COMPLIANT', 'NA'] }[rt];
  if (opts) return (
    <div className="flex gap-2 flex-wrap">
      {opts.map((o) => <button key={o} disabled={disabled} onClick={() => onChange(o)} data-testid={`qs-opt-${item.itemId}-${o}`}
        className={`px-3 py-1 rounded text-xs border ${value === o ? (o.includes('FAIL') || o === 'NO' || o === 'NON_COMPLIANT' ? 'bg-red-600 text-white' : o === 'NA' ? 'bg-slate-500 text-white' : 'bg-green-600 text-white') : 'bg-white text-slate-600'}`}>{o}</button>)}
    </div>
  );
  if (rt === 'SINGLE_SELECT') return <select disabled={disabled} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-2 py-1 text-xs"><option value="">Select…</option>{(item.options || []).map((o) => <option key={o}>{o}</option>)}</select>;
  return <input disabled={disabled} type={rt === 'NUMERIC' ? 'number' : rt === 'DATE' ? 'date' : 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-2 py-1 text-xs" data-testid={`qs-input-${item.itemId}`} />;
}

/* ------------------------------- Observations ------------------------------- */
function Observations({ domain, projectId, obsUrl, observations, team, parties, reload, accent }) {
  const isQuality = domain === 'QUALITY';
  const { queueCapture } = useOffline();
  const [creating, setCreating] = useState(false);
  const [openSafety, setOpenSafety] = useState(null);
  const [evi, setEvi] = useState([]);
  const [aiType, setAiType] = useState(isQuality ? 'WORKMANSHIP' : 'PPE');
  const aiTypes = isQuality
    ? [['WORKMANSHIP', 'General / Workmanship'], ['CRACK_DAMAGE', 'Potential Crack / Surface Damage'], ['FINISHING', 'Finishing'], ['ALIGNMENT_INSTALLATION', 'Installation Concern'], ['DESIGN_SITE_DEVIATION', 'Drawing/BIM Deviation'], ['CHECKLIST_ITEM', 'Checklist Acceptance Review']]
    : [['PPE', 'PPE'], ['HOUSEKEEPING', 'Housekeeping'], ['ACCESS_OBSTRUCTION', 'Access / Obstruction'], ['BARRICADING', 'Barricading / Edge Protection'], ['WORK_AT_HEIGHT', 'Work at Height'], ['UNSAFE_CONDITION', 'Unsafe Condition']];
  const [form, setForm] = useState(isQuality ? { type: 'DEFECT', description: '' } : { category: 'UNSAFE_CONDITION', description: '' });
  const create = async () => {
    if (!form.description.trim()) return toast.error('Description required');
    // Offline: queue the governed operation (+ any photos captured offline) for conflict-aware sync.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const photos = (evi || []).filter((e) => typeof e === 'string' && e.startsWith('data:'));
      const evidenceIds = (evi || []).filter((e) => !(typeof e === 'string' && e.startsWith('data:')));
      await queueCapture({
        type: 'qs-op', projectId, label: isQuality ? 'Quality Observation' : 'Safety Observation',
        payload: { opType: isQuality ? 'quality.observation.create' : 'safety.observation.create',
                   opPayload: { ...form, evidenceIds, clientKey: newClientKey() }, photos },
      });
      toast.success('Saved offline — will sync when back online');
      setCreating(false); setEvi([]); setForm(isQuality ? { type: 'DEFECT', description: '' } : { category: 'UNSAFE_CONDITION', description: '' });
      return;
    }
    try { const r = await axios.post(`${API}/projects/${projectId}/${obsUrl}`, { ...form, evidenceIds: evi }); toast.success(`Created ${r.data.observationNumber}`); setCreating(false); setEvi([]); setForm(isQuality ? { type: 'DEFECT', description: '' } : { category: 'UNSAFE_CONDITION', description: '' }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const classify = async (o, classification) => {
    try { await axios.post(`${API}/projects/${projectId}/quality/observations/${o.id}/classify`, { classification }); toast.success('Classified'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Not allowed'); }
  };
  return (
    <div data-testid="qs-observations">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-slate-800">{isQuality ? 'Quality Observations' : 'Safety Observations'}</h2>
        <button onClick={() => setCreating(!creating)} data-testid="qs-new-observation-btn" className={`flex items-center gap-1 px-3 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}><Plus className="w-4 h-4" /> New Observation</button>
      </div>
      {creating && (
        <div className="bg-white rounded-xl border p-4 mb-4 space-y-3" data-testid="qs-observation-form">
          <select value={isQuality ? form.type : form.category} onChange={(e) => setForm({ ...form, [isQuality ? 'type' : 'category']: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qs-observation-type">
            {(isQuality ? Q_OBS_TYPES : S_OBS_CATS).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the observation…" className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} data-testid="qs-observation-desc" />
          <CameraEvidence projectId={projectId} evidenceIds={evi} onChange={setEvi} testId="qs-obs-evi" />
          <div className="flex gap-2 items-center flex-wrap">
            <button onClick={create} data-testid="qs-create-observation-btn" className={`px-4 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}>Create Observation</button>
            <select value={aiType} onChange={(e) => setAiType(e.target.value)} data-testid="qs-ai-assist-type" className="border rounded-lg px-2 py-2 text-xs">
              {aiTypes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button onClick={async () => {
              if (!evi.length) return toast.error('Capture or upload evidence first');
              try {
                await axios.post(`${API}/projects/${projectId}/qs/ai/analyze`, { domain, analysisType: aiType, sourceEvidenceIds: evi });
                toast.success('AI-assisted visual review requested — check the AI Review Queue');
              } catch (e) { toast.error(e.response?.data?.detail || 'AI assist unavailable'); }
            }} data-testid="qs-obs-ai-assist" className="px-4 py-2 border border-slate-800 text-slate-800 text-sm rounded-lg flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> AI Assist</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {observations.length === 0 && <p className="text-slate-400 text-sm">No observations yet.</p>}
        {observations.map((o) => (
          <div key={o.id} className="bg-white rounded-lg border p-3 flex items-center justify-between" data-testid={`qs-observation-row-${o.id}`}>
            <button className="text-left flex-1" onClick={() => !isQuality && setOpenSafety(o.id)}>
              <p className="font-medium text-slate-800 text-sm">{o.observationNumber} · {isQuality ? o.type : o.category}{o.classification ? ` · ${o.classification}` : ''}</p>
              <p className="text-xs text-slate-500">{o.description}</p>
            </button>
            <div className="flex items-center gap-2">
              <EvidenceButton projectId={projectId} kind="observation" obsKind={isQuality ? 'quality' : 'safety'} id={o.id} compact label="360" />
              {isQuality && !o.classification && <button onClick={() => classify(o, 'NON_CONFORMANCE_CANDIDATE')} data-testid={`qs-classify-${o.id}`} className="text-xs border rounded px-2 py-1">Mark NC Candidate</button>}
              <span className={`text-xs px-2 py-0.5 rounded ${badge(o.status)}`}>{o.status}</span>
              {!isQuality && <ChevronRight className="w-4 h-4 text-slate-400" />}
            </div>
          </div>
        ))}
      </div>
      {openSafety && <SafetyObservationDrawer projectId={projectId} obsId={openSafety} onClose={() => { setOpenSafety(null); reload(); }} />}
    </div>
  );
}

/* ------------------------------- Configuration ------------------------------- */
function Configuration({ projectId, config, setConfig, accent }) {
  const [cfg, setCfg] = useState(config || {});
  useEffect(() => { setCfg(config || {}); }, [config]);
  const save = async () => {
    try { const r = await axios.put(`${API}/projects/${projectId}/qs/config`, { numbering: cfg.numbering, separationOfDuties: cfg.separationOfDuties }); setConfig(r.data); toast.success('Configuration saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  if (!config) return <p className="text-slate-500 text-sm">Configuration is view-restricted or loading…</p>;
  const numbering = cfg.numbering || {};
  return (
    <div className="bg-white rounded-xl border p-4 space-y-4 max-w-lg" data-testid="qs-config">
      <h2 className="text-lg font-semibold text-slate-800">Configuration</h2>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Numbering patterns (tokens: {'{YYYY}'} {'{seq:5}'})</p>
        {['INSPECTION', 'QUALITY_OBS', 'SAFETY_OBS', 'NCR', 'CAPA'].map((k) => (
          <div key={k} className="flex items-center gap-2 mb-2">
            <span className="text-xs text-slate-500 w-28">{k}</span>
            <input value={numbering[k] || ''} onChange={(e) => setCfg({ ...cfg, numbering: { ...numbering, [k]: e.target.value } })} className="flex-1 border rounded px-2 py-1 text-sm" data-testid={`qs-numbering-${k}`} />
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!cfg.separationOfDuties} onChange={(e) => setCfg({ ...cfg, separationOfDuties: e.target.checked })} data-testid="qs-sod-toggle" />
        Enforce separation of duties (submitter cannot review own inspection)
      </label>
      <button onClick={save} data-testid="qs-save-config-btn" className={`px-4 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}>Save Configuration</button>
    </div>
  );
}

/* ------------------------------- Roles ------------------------------- */
function RolesPanel({ projectId, roles, team, parties, reload, accent }) {
  const [userId, setUserId] = useState('');
  const [selRoles, setSelRoles] = useState([]);
  const [partyId, setPartyId] = useState('');
  const toggle = (r) => setSelRoles(selRoles.includes(r) ? selRoles.filter((x) => x !== r) : [...selRoles, r]);
  const assign = async () => {
    if (!userId || selRoles.length === 0) return toast.error('Pick a user and at least one role');
    try { await axios.put(`${API}/projects/${projectId}/qs/roles`, { userId, roles: selRoles, partyId: partyId || null }); toast.success('Roles assigned'); setUserId(''); setSelRoles([]); setPartyId(''); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (uid) => { try { await axios.delete(`${API}/projects/${projectId}/qs/roles/${uid}`); toast.success('Removed'); reload(); } catch (e) { toast.error('Failed'); } };
  return (
    <div data-testid="qs-roles">
      <h2 className="text-lg font-semibold text-slate-800 mb-3">Team & Project Functional Roles</h2>
      <div className="bg-white rounded-xl border p-4 mb-4 space-y-3">
        <select value={userId} onChange={(e) => setUserId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qs-role-user">
          <option value="">Select user…</option>
          {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          {FUNCTIONAL_ROLES.map((r) => (
            <button key={r} onClick={() => toggle(r)} data-testid={`qs-role-opt-${r}`} className={`text-xs px-2 py-1 rounded border ${selRoles.includes(r) ? `bg-${accent}-600 text-white` : 'bg-white text-slate-600'}`}>{r}</button>
          ))}
        </div>
        <select value={partyId} onChange={(e) => setPartyId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qs-role-party">
          <option value="">Organization (optional, for contractor scope)…</option>
          {parties.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.type})</option>)}
        </select>
        <button onClick={assign} data-testid="qs-assign-role-btn" className={`px-4 py-2 bg-${accent}-600 text-white text-sm rounded-lg`}>Assign Roles</button>
      </div>
      <div className="space-y-2">
        {roles.map((r) => (
          <div key={r.userId} className="bg-white rounded-lg border p-3 flex items-center justify-between" data-testid={`qs-role-row-${r.userId}`}>
            <div>
              <p className="text-sm font-medium text-slate-800">{r.userName || r.userEmail}</p>
              <p className="text-xs text-slate-500">{(r.roles || []).join(', ')}{r.partyId ? ' · org-scoped' : ''}</p>
            </div>
            <button onClick={() => remove(r.userId)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
